import React from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import {
  AssetExportConfigurationContext,
  AssetExportConfigurationContextProps,
} from '../context/AssetExportConfigurationContext';
import { AssetExportConfiguration } from '../types/asset_export_configuration';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';

const assetExportConfigurationState = {
  items: new Map<string, AssetExportConfiguration>(),
  loading: false,
};

export const assetExportConfigurationSlice = createSlice({
  name: 'assetExportConfiguration',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      assetExportConfigurationState.items = new Map();
      assetExportConfigurationState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<AssetExportConfiguration[]>) => {
      assetExportConfigurationState.items = new Map();
      action.payload.forEach(p => assetExportConfigurationState.items.set(p.id, new AssetExportConfiguration(p)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<AssetExportConfiguration[]>) => {
      action.payload.forEach(p => assetExportConfigurationState.items.set(p.id, new AssetExportConfiguration(p)));
      assetExportConfigurationState.items = new Map(assetExportConfigurationState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<AssetExportConfiguration[]>) => {
      action.payload.forEach(p => assetExportConfigurationState.items.delete(p.id));
      assetExportConfigurationState.items = new Map(assetExportConfigurationState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      assetExportConfigurationState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface AssetExportConfigurationProviderProps {
  children: React.ReactNode;
}

export default function AssetExportConfigurationProvider({ children }: AssetExportConfigurationProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.assetExportConfigurations.key);

  const assetExportConfigurationContext: AssetExportConfigurationContextProps = {
    assetExportConfigurations: assetExportConfigurationState.items,
    assetExportConfigurationsLoading: assetExportConfigurationState.loading,
    setAssetExportConfigurations: (assetExportConfigurations: Map<string, AssetExportConfiguration>) => {
      dispatch(assetExportConfigurationSlice.actions.set([...assetExportConfigurations.values()]));
    },
  };

  return (
    <AssetExportConfigurationContext.Provider value={assetExportConfigurationContext}>
      {children}
    </AssetExportConfigurationContext.Provider>
  );
}
